import React from 'react';
import JsonLd from '../../components/JsonLd';
import SEOHead from '../../components/SEOHead';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import {
  ShieldCheck, Phone, Database, AlertTriangle, KeyRound, FileText, ArrowRight, CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const IdentityVerificationPage = () => {
  const benefits = [
    {
      icon: ShieldCheck,
      title: 'Verification policies by segment and risk level',
      description:
        'Configure different verification tiers - OTP, KBA, email confirmation - for each action type, customer segment, and risk score, ensuring the right level of assurance for every change.',
    },
    {
      icon: Database,
      title: 'Real-time CRM actions with confirmation',
      description:
        'Once verified, the AI executes the update directly in the CRM and linked systems - no agent involvement - and sends the customer a confirmation via their preferred channel.',
    },
    {
      icon: FileText,
      title: 'Audit logs and safe escalation on risk flags',
      description:
        'Every verification attempt and action is logged. Suspicious patterns, repeated failures, or high-risk changes trigger automatic escalation to a human agent with full context.',
    },
  ];

  const capabilities = [
    'Integrate CRM + identity verification (OTP/email/SMS/KBA) + notification service',
    'Define verification tiers: which actions require which level of verification',
    'Configure flows: address update, profile change, account recovery',
    'Configure fraud protections: rate limits, suspicious patterns, escalation rules',
    'Pilot top account update intents → expand to full self-service',
  ];

  const features = [
    {
      icon: ShieldCheck,
      title: 'Multi-Tier Identity Verification',
      desc: 'Define verification strength per action type - low-risk changes use email confirmation while high-risk updates require OTP plus knowledge-based verification.',
      gradient: 'from-indigo-500 to-violet-500',
    },
    {
      icon: Phone,
      title: 'OTP / KBA / SMS Verification',
      desc: 'Support one-time passwords, security questions, SMS codes, and email links - configurable by customer segment, channel, and risk level.',
      gradient: 'from-violet-500 to-purple-500',
    },
    {
      icon: Database,
      title: 'Real-Time CRM Updates',
      desc: 'Execute address changes, profile updates, preference changes, and account recovery actions directly in the CRM and linked systems upon successful verification.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: AlertTriangle,
      title: 'Fraud Detection & Rate Limits',
      desc: 'Monitor for suspicious patterns - repeated failures, velocity anomalies, unusual change requests - and apply rate limits or trigger escalation automatically.',
      gradient: 'from-pink-500 to-rose-500',
    },
    {
      icon: KeyRound,
      title: 'Account Recovery Flows',
      desc: 'Guided self-service account recovery using configurable multi-step verification - restoring access securely without requiring an agent.',
      gradient: 'from-rose-500 to-red-400',
    },
    {
      icon: FileText,
      title: 'Audit Trail Logging',
      desc: 'Every verification step, action taken, and outcome is logged with timestamp, channel, and method - available for compliance review and fraud investigation.',
      gradient: 'from-orange-400 to-amber-400',
    },
  ];

  const steps = [
    {
      step: '01',
      title: 'Customer requests an account action',
      desc: 'The customer contacts via voice, chat, or email and requests an account update - address change, profile modification, preference update, or account recovery.',
    },
    {
      step: '02',
      title: 'AI verifies identity per policy',
      desc: 'Based on the action type and risk level, the AI initiates the appropriate verification method: OTP, email link, SMS code, or knowledge-based challenge.',
    },
    {
      step: '03',
      title: 'AI executes update in CRM and linked systems',
      desc: 'Upon successful verification, the agent applies the change directly in the CRM and any linked systems - instantly and without agent involvement.',
    },
    {
      step: '04',
      title: 'AI confirms via notification and logs the case',
      desc: 'The customer receives a confirmation notification via their preferred channel. A case record with the full verification and action history is created.',
    },
    {
      step: '05',
      title: 'Escalates when verification fails or risk flags trigger',
      desc: 'If verification cannot be completed or a fraud pattern is detected, the session escalates to a human agent with the full context and verification history.',
    },
  ];

  const stats = [
    { value: '~80%', label: 'Queries resolved without agent' },
    { value: '65%', label: 'Self-service adoption (up from 22%)' },
    { value: '~88%', label: 'Routing accuracy' },
    { value: '+22%', label: 'CSAT uplift' },
  ];

  const faqs = [
    {
      q: 'Can verification rules vary by geography?',
      a: 'Yes. Verification tiers and methods are configurable by country, region, and regulatory context. For example, GDPR-regulated markets may require additional confirmation steps for data changes, while other regions use simpler flows - all managed in the same policy layer.',
    },
    {
      q: 'Can it support sensitive changes like adding a payment method or changing an email address?',
      a: 'Yes. Sensitive changes are assigned higher verification tiers - requiring stronger authentication before execution. You define which actions are sensitive and what verification level they demand. The AI enforces these rules on every interaction.',
    },
    {
      q: 'What verification methods are supported?',
      a: 'Digian.ai supports SMS OTP, email OTP, knowledge-based authentication (KBA), email link confirmation, and integration with identity providers for SSO-based verification. Methods are configured per action type and customer segment.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="useCaseIdentityVerification" />
      <JsonLd schema={{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Home","item":"https://digian.ai"},{"@type":"ListItem","position":2,"name":"Use Cases","item":"https://digian.ai/use-cases"},{"@type":"ListItem","position":3,"name":"Identity Verification","item":"https://digian.ai/use-cases/identity-verification"}]}} />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <p className="text-sm font-medium tracking-wide text-indigo-600 uppercase">Customer Care / Identity Verification &amp; Account Updates</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900">
              Secure Account Updates - Identity Verification + CRM Actions
            </h1>
            <p className="text-lg text-slate-600">
              Enable customers to securely update profile details, addresses, preferences, and account recovery - across voice, chat, and email - with policy-based verification and real-time CRM updates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/demo"><Button size="lg">Request a Demo</Button></Link>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              {['Multi-tier verification', 'Real-time CRM updates', 'Fraud detection built in'].map(t => (
                <span key={t} className="flex items-center gap-1.5 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />{t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-indigo-600 uppercase">Secure Account Self-Service</p>
                <p className="text-sm text-slate-600">Policy-verified account updates across all channels</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-slate-700">
              {capabilities.map((item, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <ShieldCheck className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-indigo-600 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <motion.div key={s.label} {...fadeUp} transition={{ delay: i * 0.1 }}>
                <p className="text-3xl font-extrabold text-white">{s.value}</p>
                <p className="text-indigo-200 text-sm mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Why teams choose secure AI account self-service</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div key={b.title} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Capabilities</p>
            <h2 className="text-3xl font-bold text-slate-900">Everything needed for secure account self-service</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={f.title} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all group">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-4`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-indigo-700 transition-colors">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Process</p>
            <h2 className="text-3xl font-bold text-slate-900">How secure account self-service works at runtime</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={s.step} {...fadeUp} transition={{ delay: i * 0.12 }}
                className="bg-white rounded-2xl border border-slate-200 p-8">
                <div className="flex items-center gap-4 mb-5">
                  <span className="text-5xl font-black text-indigo-100">{s.step}</span>
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">80% of account queries resolved - no agent needed</h2>
            <p className="text-xl text-indigo-100">
              Secure self-service for every account action. Policy-verified. Audit-logged. Always available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo"><Button size="lg" variant="secondary">Request Demo</Button></Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                  Talk to Our Team <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <p className="text-indigo-200 text-sm">Multi-tier verification · Real-time CRM updates · Fraud detection</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default IdentityVerificationPage;
